import {Image} from '@jobseeker/lotus/components/Image';
import {Box, Button, IconButton, Typography} from '@mui/material';
import {Tooltip} from '@jobseeker/lotus/elements';
import {HiristSaveJob} from '@jobseeker/lotus/assets/icons';
import {Loader} from '@jobseeker/lotus/components';
import {useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';

type ApplyBtnsProps = {
  handleApply: () => void;
  saveJobForLogged: () => void;
  openInsightsDialog: () => void;
  handleJobFollowup: (jobId: string) => void;
  showLoader?: boolean;
  setShowLoader?: React.Dispatch<React.SetStateAction<boolean>>;
  setApplyBtnClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  jobId: string;
  jobSaved?: boolean;
  jobApplied?: boolean;
  jobFollowedUp?: boolean;
  showApply?: boolean;
  branded?: boolean;
};

export const ApplyButton = ({
  handleApply,
  showLoader,
  setShowLoader,
  jobApplied = false,
  setApplyBtnClicked,
  branded,
}: Partial<ApplyBtnsProps>) => {
  const profile = useSelector(
    (state: AppState) => state.user.profile.profileUpdateCohorts
  );
  const functionalArea = useSelector(
    (state: AppState) => state.user.profile.functionalArea
  );
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  return (
    <Button
      variant="contained"
      disableRipple
      sx={{
        fontSize: 16,
        mr: 1.25,
        fontWeight: 700,
        width: {xxs: '100%', sm: branded ? 120 : 91},
        ':hover': {bgcolor: '#E9630C'},
        border: 'none',
        color: 'white !important',
        ':disabled': {
          bgcolor: '#E9630C',
        },
      }}
      onClick={() => {
        setApplyBtnClicked?.(true);

        const willShowFunctionalAreaModal =
          !isAppIimjobs &&
          (functionalArea?.id === 1 || functionalArea?.id === 6);

        if (
          !Object.values(profile || {}).some(value => value === true) &&
          !willShowFunctionalAreaModal
        ) {
          setShowLoader && setShowLoader(true);
        }

        handleApply?.();
      }}
      disabled={jobApplied || showLoader}
    >
      {jobApplied ? (
        'Applied'
      ) : showLoader ? (
        <Loader color="inherit" size={28} />
      ) : (
        'Apply'
      )}
    </Button>
  );
};

export const InsightsButton = ({
  openInsightsDialog,
  branded,
}: Partial<ApplyBtnsProps>) => {
  return (
    <Button
      disableRipple
      variant="outlined"
      sx={{
        fontSize: 16,
        mr: 1.25,
        fontWeight: 700,
        border: '1px solid #E9630C',
        ':hover': {bgcolor: '#E9630C', color: 'white'},
        width: {xxs: 'auto', md: branded ? '120px' : 'auto'},
      }}
      onClick={() => {
        openInsightsDialog?.();
      }}
    >
      Insights
    </Button>
  );
};

export const FollowUpButton = ({
  jobApplied,
  jobId = '0',
  jobFollowedUp,
  handleJobFollowup,
  branded,
}: Partial<ApplyBtnsProps>) => {
  return (
    <Tooltip
      placement="bottom-start"
      arrow
      title={
        !jobApplied && (
          <Typography
            variant="subtitle3"
            lineHeight="16px"
            sx={{
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
          >
            You need to apply to this job before following up.
          </Typography>
        )
      }
    >
      <Button
        disableRipple
        variant="outlined"
        sx={{
          fontSize: 16,
          fontWeight: 700,
          border: '1px solid #E9630C',
          ':hover': {bgcolor: '#E9630C !important', color: 'white'},
          width: {xxs: 'auto', md: branded ? '120px' : 'auto'},
        }}
        onClick={() => {
          handleJobFollowup?.(jobId);
        }}
        // disabled={jobFollowedUp || !jobApplied}
      >
        {jobFollowedUp ? 'Followed-up' : 'Follow-up'}
      </Button>
    </Tooltip>
  );
};

export const SaveJobButton = ({
  saveJobForLogged,
  jobSaved,
}: Partial<ApplyBtnsProps>) => {
  return (
    <Tooltip
      placement="bottom-end"
      arrow
      title={
        <Typography
          variant="subtitle3"
          lineHeight="16px"
          sx={{
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
          }}
        >
          Save this job for future reference
        </Typography>
      }
    >
      <IconButton
        disableRipple
        sx={{
          border: '1px solid #E9630C',
          borderRadius: '4px',
          bgcolor: jobSaved ? '#E9630C' : 'transparent',
          ':hover': {bgcolor: jobSaved ? '#E9630C' : 'transparent'},
        }}
        onClick={() => {
          saveJobForLogged?.();
        }}
      >
        <Image src={HiristSaveJob} height={22} width={25} alt="save-job" />
      </IconButton>
    </Tooltip>
  );
};

const Buttons = ({
  handleApply,
  saveJobForLogged,
  openInsightsDialog,
  handleJobFollowup,
  showLoader,
  setShowLoader,
  jobId = '0',
  jobSaved = false,
  jobApplied = false,
  jobFollowedUp = false,
  showApply = false,
  setApplyBtnClicked,
  branded,
}: ApplyBtnsProps) => (
  <Box mt={3} display="flex" justifyContent="space-between">
    <Box>
      {showApply && (
        <ApplyButton
          handleApply={handleApply}
          showLoader={showLoader}
          setShowLoader={setShowLoader}
          jobApplied={jobApplied}
          setApplyBtnClicked={setApplyBtnClicked}
          branded={branded ? true : false}
        />
      )}
      <InsightsButton
        openInsightsDialog={openInsightsDialog}
        branded={branded ? true : false}
      />
      <FollowUpButton
        jobFollowedUp={jobFollowedUp}
        jobApplied={jobApplied}
        handleJobFollowup={handleJobFollowup}
        jobId={jobId}
        branded={branded ? true : false}
      />
    </Box>
    <SaveJobButton saveJobForLogged={saveJobForLogged} jobSaved={jobSaved} />
  </Box>
);

export default Buttons;
